import { ChevronRight, Eye, Loader2, RotateCcw, Search, Send, TriangleAlert, Upload } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { type Step, StepperNav, useStepper } from "@/components/ui/stepper";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAdminKey } from "@/hooks/use-auth";
import { adminApi, ApiError, DM_BUILTIN_VARIABLES, type SlackDmPreview, type SlackDmRecipient, type SlackDmResult } from "@/lib/api";
import { parseValues } from "@/lib/dm-values";
import { cn } from "@/lib/utils";

const PLACEHOLDER = `Hi {{name}},

Your API key for {{course}} is:
{{api_key}}

Keep it to yourself — submissions are attributed with it.`;

/**
 * Bulk direct messages, as two steps: write, then review and send.
 *
 * The split is the safety. Sixty DMs cannot be recalled, and the rendered text -- with a
 * student's own key substituted in -- is only visible in the preview, so there is no
 * path to "Send" that does not pass through it. The preview is stored with the inputs it
 * was computed from; editing anything sends the operator back to step one, rather than
 * leaving a Send button live against a message that no longer exists.
 *
 * Recipients are chosen in the review, not guessed at in the composer: every reachable
 * student is ticked to start with, and the operator unticks -- or filters and ticks a
 * handful. The server re-renders for exactly the ticked addresses.
 */
export function DmComposer({ courseId }: { courseId: string }) {
  const adminKey = useAdminKey();
  const [template, setTemplate] = useState(PLACEHOLDER);
  const [valuesText, setValuesText] = useState("");
  const [includeStaff, setIncludeStaff] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  /**
   * The preview with the inputs it was computed from. Staleness is derived by comparing
   * those against the current inputs rather than cleared by an effect, which would leave
   * one frame where Send is enabled against the previous template.
   */
  const [snapshot, setSnapshot] = useState<{
    signature: string;
    preview: SlackDmPreview;
    selected: Set<string>;
    results: SlackDmResult[] | null;
  } | null>(null);

  const parsed = parseValues(valuesText);
  const signature = JSON.stringify([courseId, includeStaff, template, valuesText]);
  const fresh = snapshot?.signature === signature ? snapshot : null;
  const preview = fresh?.preview ?? null;
  const results = fresh?.results ?? null;
  const selected = fresh?.selected ?? new Set<string>();

  const reachable = (r: SlackDmRecipient) => !r.blocked && r.missing.length === 0;
  const chosen = preview?.recipients.filter((r) => reachable(r) && selected.has(r.email)) ?? [];

  const steps: Step[] = [
    {
      id: "compose",
      title: "Write",
      incomplete: [
        ...(template.trim() ? [] : ["a message"]),
        ...(parsed.error ? [`valid per-person values (${parsed.error})`] : []),
      ],
    },
    {
      id: "review",
      title: results ? "Sent" : "Review and send",
      incomplete: preview ? [] : ["a preview of the current message"],
    },
  ];
  const stepper = useStepper(steps);
  const input = { template, values: parsed.values, include_staff: includeStaff };

  async function runPreview() {
    if (!adminKey) return;
    setBusy(true);
    try {
      const result = await adminApi.slackDmPreview(courseId, input, adminKey);
      setSnapshot({
        signature,
        preview: result,
        // Everyone reachable, to start; the operator unticks.
        selected: new Set(result.recipients.filter(reachable).map((r) => r.email)),
        results: null,
      });
      stepper.next();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed", { duration: 12000 });
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    if (!adminKey || !preview || !chosen.length) return;
    setBusy(true);
    try {
      const result = await adminApi.slackDmSend(courseId, { ...input, emails: chosen.map((r) => r.email) }, adminKey);
      setSnapshot((prev) => (prev ? { ...prev, results: result.results } : prev));
      if (result.failed === 0) toast.success(`Sent ${result.sent} message${result.sent === 1 ? "" : "s"}`);
      else toast.warning(`Sent ${result.sent}, ${result.failed} failed`, { duration: 12000 });
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed", { duration: 12000 });
    } finally {
      setBusy(false);
    }
  }

  function setSelected(update: (prev: Set<string>) => Set<string>) {
    setSnapshot((prev) => (prev ? { ...prev, selected: update(prev.selected) } : prev));
  }

  function startOver() {
    setSnapshot(null);
    stepper.reset();
  }

  return (
    <div className="space-y-4">
      <StepperNav stepper={stepper} />

      {stepper.index === 0 ? (
        <Card>
          <CardContent className="grid gap-6 lg:grid-cols-[1fr_20rem]">
            <div className="space-y-2">
              <Label htmlFor="dm-template">Message</Label>
              <textarea
                id="dm-template"
                rows={11}
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                className="border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 w-full resize-y rounded-md border px-3 py-2 font-mono text-sm outline-none focus-visible:ring-3"
              />
              <p className="text-muted-foreground text-xs">
                From each student's record:{" "}
                {DM_BUILTIN_VARIABLES.map((variable) => (
                  <button
                    key={variable}
                    type="button"
                    className="bg-muted hover:bg-muted/70 mr-1 rounded px-1 font-mono"
                    onClick={() => setTemplate((t) => `${t}{{${variable}}}`)}
                    title={`Append {{${variable}}}`}
                  >
                    {`{{${variable}}}`}
                  </button>
                ))}
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Recipients</Label>
                <p className="text-muted-foreground text-xs">
                  This course's students, chosen one by one in the next step. Slack only supplies the account to deliver to;
                  anyone without one is shown, not skipped.
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <Checkbox id="dm-staff" checked={includeStaff} onCheckedChange={(next) => setIncludeStaff(next === true)} />
                  <Label htmlFor="dm-staff" className="font-normal">
                    Include staff
                  </Label>
                </div>
              </div>

              <Collapsible open={advanced || Boolean(valuesText)} onOpenChange={setAdvanced}>
                <CollapsibleTrigger className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs">
                  <ChevronRight className={cn("size-3.5 transition-transform", (advanced || valuesText) && "rotate-90")} aria-hidden />
                  Per-person values
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-2 pt-2">
                  <textarea
                    id="dm-values"
                    rows={6}
                    value={valuesText}
                    onChange={(e) => setValuesText(e.target.value)}
                    placeholder={'{\n  "alice@nyu.edu": { "grade": "A" },\n  "bob@nyu.edu":   { "grade": "B" }\n}'}
                    className="border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 w-full resize-y rounded-md border px-3 py-2 font-mono text-xs outline-none focus-visible:ring-3"
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    {/* A label rather than a Button, because the file input needs a label to
                        trigger it and Button does not forward asChild. */}
                    <label className={cn(buttonVariants({ variant: "outline", size: "sm" }), "cursor-pointer")}>
                      <Upload className="size-4" aria-hidden />
                      Import JSON
                      <input
                        type="file"
                        accept=".json,application/json"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) setValuesText(await file.text());
                        }}
                      />
                    </label>
                    {parsed.error ? <p className="text-destructive text-xs">{parsed.error}</p> : null}
                  </div>
                  <p className="text-muted-foreground text-xs">
                    Keyed by email. Extra variables for <code className="font-mono">{"{{grade}}"}</code>-style holes; the
                    built-ins above always win, so a stale export cannot send anyone someone else's key.
                  </p>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </CardContent>
          <div className="flex items-center justify-between gap-4 border-t px-6 py-4">
            <p className="text-muted-foreground text-xs">
              {stepper.blocked.length ? `Still needed: ${stepper.blocked.join(", ")}` : "Nothing is sent from this step."}
            </p>
            <Button onClick={runPreview} disabled={busy || !stepper.canAdvance}>
              {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Eye className="size-4" aria-hidden />}
              Preview
            </Button>
          </div>
        </Card>
      ) : preview ? (
        <ReviewStep
          preview={preview}
          results={results}
          selected={selected}
          setSelected={setSelected}
          reachable={reachable}
          busy={busy}
          onBack={stepper.back}
          onSend={send}
          onStartOver={startOver}
        />
      ) : null}
    </div>
  );
}

function ReviewStep({
  preview,
  results,
  selected,
  setSelected,
  reachable,
  busy,
  onBack,
  onSend,
  onStartOver,
}: {
  preview: SlackDmPreview;
  results: SlackDmResult[] | null;
  selected: Set<string>;
  setSelected: (update: (prev: Set<string>) => Set<string>) => void;
  reachable: (r: SlackDmRecipient) => boolean;
  busy: boolean;
  onBack: () => void;
  onSend: () => void;
  onStartOver: () => void;
}) {
  const [query, setQuery] = useState("");
  const byEmail = useMemo(() => new Map((results ?? []).map((r) => [r.email, r])), [results]);

  const needle = query.trim().toLowerCase();
  const rows = preview.recipients.filter(
    (r) => !needle || r.email.toLowerCase().includes(needle) || r.name.toLowerCase().includes(needle),
  );
  const reachableRows = rows.filter(reachable);
  const allShownChosen = reachableRows.length > 0 && reachableRows.every((r) => selected.has(r.email));
  const chosenCount = preview.recipients.filter((r) => reachable(r) && selected.has(r.email)).length;
  const blockedCount = preview.recipients.length - preview.recipients.filter(reachable).length;
  const sent = results?.filter((r) => r.sent).length ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">
          {results ? `Delivered to ${sent} of ${results.length}` : `${chosenCount} of ${preview.recipients.length} selected`}
          {!results && blockedCount ? <span className="text-muted-foreground font-normal"> · {blockedCount} unreachable</span> : null}
        </CardTitle>
        <CardDescription className="text-xs">
          {results
            ? "Each row shows what Slack said. Failures are per person; nobody else was held up by them."
            : "Exactly this text goes to exactly the ticked people. Untick anyone who should not get it, or filter and tick a few."}
        </CardDescription>
        {preview.unknown_value_keys.length > 0 ? (
          <p className="text-destructive flex items-start gap-2 text-xs">
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            <span>
              Values were supplied for addresses not enrolled here, so they were not used: {preview.unknown_value_keys.join(", ")}.
              Often a stale export, or the wrong course.
            </span>
          </p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {!results ? (
          <div className="relative sm:max-w-xs">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" aria-hidden />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter by name or email" className="pl-8" />
          </div>
        ) : null}
        <Table>
          <TableHeader>
            <TableRow>
              {!results ? (
                <TableHead className="w-10">
                  <Checkbox
                    aria-label="Select everyone shown"
                    checked={allShownChosen}
                    indeterminate={!allShownChosen && reachableRows.some((r) => selected.has(r.email))}
                    onCheckedChange={(next) =>
                      setSelected((prev) => {
                        const out = new Set(prev);
                        for (const r of reachableRows) {
                          if (next === true) out.add(r.email);
                          else out.delete(r.email);
                        }
                        return out;
                      })
                    }
                  />
                </TableHead>
              ) : null}
              <TableHead>Student</TableHead>
              <TableHead>Message</TableHead>
              <TableHead className="w-44">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((recipient) => {
              const result = byEmail.get(recipient.email);
              const problem = recipient.blocked || (recipient.missing.length ? `Unresolved: ${recipient.missing.join(", ")}` : "");
              const ticked = selected.has(recipient.email) && reachable(recipient);
              return (
                <TableRow key={recipient.email} className={cn(!results && !ticked && "opacity-60")}>
                  {!results ? (
                    <TableCell className="align-top">
                      <Checkbox
                        aria-label={`Send to ${recipient.email}`}
                        checked={ticked}
                        disabled={!reachable(recipient)}
                        onCheckedChange={() =>
                          setSelected((prev) => {
                            const out = new Set(prev);
                            if (out.has(recipient.email)) out.delete(recipient.email);
                            else out.add(recipient.email);
                            return out;
                          })
                        }
                      />
                    </TableCell>
                  ) : null}
                  <TableCell className="align-top text-sm">
                    <p className="flex items-center gap-1.5">
                      {recipient.name || recipient.email}
                      {recipient.role === "staff" ? (
                        <Badge variant="outline" className="text-[10px]">
                          staff
                        </Badge>
                      ) : null}
                    </p>
                    <p className="text-muted-foreground font-mono text-xs">{recipient.email}</p>
                  </TableCell>
                  <TableCell className="align-top">
                    {/* Verbatim, wrapping preserved. Unresolved variables stay visible as
                        {{name}} so the hole is obvious rather than a gap to notice. */}
                    <pre className="text-muted-foreground max-w-prose font-sans text-xs whitespace-pre-wrap">{recipient.text}</pre>
                  </TableCell>
                  <TableCell className="align-top">
                    {result ? (
                      result.sent ? (
                        <Badge variant="secondary">Sent</Badge>
                      ) : (
                        <div className="space-y-1">
                          <Badge variant="destructive">Failed</Badge>
                          <p className="text-muted-foreground text-xs">{result.error}</p>
                        </div>
                      )
                    ) : results ? (
                      <Badge variant="outline">Not sent</Badge>
                    ) : problem ? (
                      <div className="space-y-1">
                        <Badge variant="destructive">Unreachable</Badge>
                        <p className="text-muted-foreground text-xs">{problem}</p>
                      </div>
                    ) : ticked ? (
                      <Badge variant="secondary">Will send</Badge>
                    ) : (
                      <Badge variant="outline">Skipped</Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
      <div className="flex items-center justify-between gap-4 border-t px-6 py-4">
        {results ? (
          <>
            <p className="text-muted-foreground text-xs">Done. The message above is what was sent.</p>
            <Button variant="outline" onClick={onStartOver}>
              <RotateCcw className="size-4" aria-hidden />
              Write another
            </Button>
          </>
        ) : (
          <>
            <p className="text-muted-foreground text-xs">
              {chosenCount ? `Goes to ${chosenCount} ${chosenCount === 1 ? "person" : "people"}. This cannot be recalled.` : "Nobody is selected."}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onBack} disabled={busy}>
                Back to edit
              </Button>
              <Button onClick={onSend} disabled={busy || chosenCount === 0}>
                {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Send className="size-4" aria-hidden />}
                Send to {chosenCount}
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
